import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import {
  AutoComplete,
  ReadOnly,
  useDataProvider,
  useEntityPermission,
  useLocalization,
} from '@ui-framework/index';

const CompanyMultiSelect = forwardRef(({ field, ...props }, ref) => {
  const { control } = useFormContext();
  const { field: input } = useController({
    name: props.name || field.name,
    control,
  });
  const provider = useDataProvider(field.provider);
  const { formatMessage } = useLocalization();
  const { isHidden, isReadOnly } = useEntityPermission();
  const [companies, setCompanies] = useState([]);
  const [failed, setFailed] = useState(false);
  const requestNumber = useRef(0);
  const ids = (input.value || []).join(',');
  const getOptions = useCallback(
    async query => {
      const response = await provider.get(
        `/issues-tracking/v1/${field.apiPath}`,
        {
          companyType: field.companyType,
          limit: 100,
          ...query,
        },
      );
      if (!response?.ok) throw new Error('Company lookup failed');
      return response.data.data.map(company => ({
        value: company.id,
        label: company.companyName,
      }));
    },
    [provider, field.apiPath, field.companyType],
  );

  useEffect(() => {
    const request = requestNumber.current;
    setFailed(false);
    if (ids) {
      getOptions({ ids })
        .then(options => {
          if (request === requestNumber.current) setCompanies(options);
        })
        .catch(() => {
          if (request === requestNumber.current) setFailed(true);
        });
    } else setCompanies([]);
    return () => {
      requestNumber.current += 1;
    };
  }, [ids, getOptions]);

  const loadOptions = async search => {
    requestNumber.current += 1;
    const request = requestNumber.current;
    try {
      const options = await getOptions({ search });
      if (request === requestNumber.current) {
        setFailed(false);
        setCompanies(current => [
          ...current.filter(
            company => !options.some(option => option.value === company.value),
          ),
          ...options,
        ]);
      }
      return options;
    } catch (error) {
      if (request === requestNumber.current) setFailed(true);
      return [];
    }
  };
  const selected = (input.value || []).map(
    id =>
      companies.find(company => company.value === id) || {
        value: id,
        label: id,
      },
  );
  if (isHidden(props.permission)) return null;
  if (isReadOnly(props.permission, input.value))
    return (
      <ReadOnly name={input.name}>
        {selected.map(option => option.label).join(', ')}
      </ReadOnly>
    );
  return (
    <AutoComplete
      {...input}
      {...props}
      ref={ref}
      control={control}
      isMulti
      defaultOptions
      loadOptions={loadOptions}
      error={
        props.error ||
        (failed
          ? formatMessage({
              id: 'COMPANY_MULTI_SELECT_LOAD_ERROR',
              defaultMessage:
                'Companies could not be loaded. Type to search again.',
            })
          : '')
      }
      overwriteProps={{
        value: selected,
        onChange: options => {
          setCompanies(options || []);
          input.onChange((options || []).map(option => option.value));
        },
      }}
    />
  );
});

export default CompanyMultiSelect;
